// ARQUIVO ATUALIZADO
import React, { useState, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { DragHandleIcon, DeleteIcon, SparklesIcon, AddIcon, CheckIcon } from '../ui/Icons';
import { callGeminiForHourEstimation } from '../../services/api';
import useToast from '../../hooks/useToast';

const ItemType = 'QUOTE_ITEM';

const DraggableQuoteItem = ({ item, index, moveItem, handleItemChange, removeItem, hourlyRate }) => {
    const ref = useRef(null);
    const [isEstimating, setIsEstimating] = useState(false);
    const [newSubItemText, setNewSubItemText] = useState('');
    const { showToast } = useToast();

    const handleEstimateHours = async () => {
        if (hourlyRate <= 0) {
            showToast("Configure seu valor/hora nas Configurações primeiro.", "error");
            return;
        }
        if (!item.description) {
            showToast("Adicione uma descrição antes de estimar.", "error");
            return;
        }
        setIsEstimating(true);
        const estimatedHours = await callGeminiForHourEstimation(item.description);
        handleItemChange(index, 'hours', estimatedHours);
        setIsEstimating(false);
    };

    const itemSubtotal = (item.hours || 0) * (item.qty || 1) * hourlyRate;

    const [, drop] = useDrop({
        accept: ItemType,
        hover(draggedItem) {
            if (!ref.current || draggedItem.index === index) return;
            moveItem(draggedItem.index, index);
            draggedItem.index = index;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: ItemType,
        item: { id: item.id, index },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    preview(drop(ref));

    // --- INÍCIO DA ALTERAÇÃO ---
    const handleAddSubItem = () => {
        if (newSubItemText.trim() === '') return;
        const newSubItems = [...(item.subItems || []), { id: Date.now(), text: newSubItemText }];
        handleItemChange(index, 'subItems', newSubItems);
        setNewSubItemText('');
    };

    const handleRemoveSubItem = (subIndex) => {
        const newSubItems = (item.subItems || []).filter((_, i) => i !== subIndex);
        handleItemChange(index, 'subItems', newSubItems);
    };
    // --- FIM DA ALTERAÇÃO ---

    return (
        <div ref={ref} style={{ opacity: isDragging ? 0.5 : 1 }} className="group relative flex flex-col py-4 px-2 border-b border-white/10 transition-colors hover:bg-white/5 rounded-lg">
            {/* --- LINHA PRINCIPAL: DESCRIÇÃO E VALOR --- */}
            <div className="flex items-start gap-3 w-full">
                <div ref={drag} className="cursor-move text-gray-500 pt-2 opacity-50 group-hover:opacity-100 transition-opacity duration-200">
                    <DragHandleIcon size={20} />
                </div>

                <textarea
                    rows="2"
                    placeholder="Descrição do serviço..." 
                    value={item.description} 
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)} 
                    className="flex-grow bg-transparent p-2 rounded-md border-2 border-transparent focus:border-white/20 focus:bg-black/20 focus:outline-none text-sm transition-colors resize-y"
                />

                <p className="w-36 text-right text-white font-semibold text-lg pt-1">
                    {itemSubtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </p>

                <button 
                    onClick={() => removeItem(index)} 
                    className="p-2 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all duration-200"
                >
                    <DeleteIcon size={20} />
                </button>
            </div>

            {/* --- LINHA DE DETALHES: CONTROLES E INPUTS --- */}
            <div className="flex items-center gap-4 pl-10 mt-2">
                <button 
                    onClick={handleEstimateHours}
                    disabled={isEstimating || !item.description}
                    className="flex items-center gap-1.5 p-1 text-indigo-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Estimar horas com IA"
                >
                    {isEstimating ? <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin"></div> : <SparklesIcon size={16} />}
                    <span className="text-xs font-semibold">Estimar</span>
                </button>

                <div className="flex items-baseline bg-black/20 rounded-md transition-colors focus-within:bg-black/40">
                    <input 
                        type="number" 
                        title="Horas"
                        placeholder="0"
                        value={item.hours || ''} 
                        onChange={(e) => handleItemChange(index, 'hours', parseFloat(e.target.value) || 0)} 
                        className="w-20 bg-transparent p-2 text-center text-white text-base no-spinner focus:outline-none"
                    />
                    <span className="text-gray-400 font-medium text-sm pr-3">h</span>
                </div>
            
                <div className="bg-black/20 rounded-md transition-colors focus-within:bg-black/40">
                    <input 
                        type="number" 
                        title="Quantidade"
                        placeholder="1"
                        value={item.qty || ''} 
                        onChange={(e) => handleItemChange(index, 'qty', parseInt(e.target.value, 10) || 1)} 
                        className="w-16 bg-transparent p-2 rounded-md text-center text-white text-base no-spinner focus:outline-none"
                    />
                </div>
            </div>
            
            {/* --- INÍCIO DA ALTERAÇÃO: Seção de Sub-itens --- */}
            <div className="pl-10 mt-4">
                <div className="space-y-2">
                    {(item.subItems || []).map((subItem, subIndex) => (
                        <div key={subItem.id} className="flex items-center gap-2 group/sub">
                            <CheckIcon size={14} className="text-gray-500" />
                            <input
                                type="text"
                                value={subItem.text}
                                readOnly
                                className="flex-1 bg-transparent text-sm text-gray-300 py-1 border-b border-transparent"
                            />
                            <button onClick={() => handleRemoveSubItem(subIndex)} className="text-red-500 opacity-0 group-hover/sub:opacity-100">
                                <DeleteIcon size={14} />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                    <AddIcon size={14} className="text-gray-500" />
                    <input
                        type="text"
                        placeholder="Adicionar sub-item (checklist)..."
                        value={newSubItemText}
                        onChange={(e) => setNewSubItemText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSubItem())}
                        className="flex-1 bg-transparent text-sm text-gray-400 placeholder-gray-500 focus:text-white py-1 border-b border-white/10 focus:border-indigo-400 focus:outline-none transition"
                    />
                </div>
            </div>
            {/* --- FIM DA ALTERAÇÃO --- */}
        </div>
    );
};

export default DraggableQuoteItem;